'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import { useAddCartLines } from '@/entities/cart/client';
import type { Product } from '@/entities/product';
import { ApiError } from '@/shared/api';
import { formatMoney } from '@/shared/lib/money';
import { Icon } from '@/shared/ui/icon';

import styles from './gift-set-builder.module.scss';

type Choice = { candle: string | null; carrier: string | null };
type OptionState = 'on' | 'idle' | 'off';

/** Gift set 06B-1: two choices with availability per combination; the set goes to the cart as one line. */
export function GiftSetBuilder({ product }: { product: Product }) {
  const bundle = product.bundle;
  const [choice, setChoice] = useState<Choice>({ candle: null, carrier: null });
  const [message, setMessage] = useState('');
  const [soldOut, setSoldOut] = useState<Record<string, boolean>>({});
  const [addedSku, setAddedSku] = useState<string | null>(null);
  const add = useAddCartLines();
  if (!bundle) return null;
  const [candleChoice, carrierChoice] = bundle.choices;
  if (!candleChoice || !carrierChoice) return null;

  const configs = product.variants.map((variant) => ({
    sku: variant.sku,
    candle: variant.bundleOptions?.[candleChoice.id] ?? null,
    carrier: variant.bundleOptions?.[carrierChoice.id] ?? null,
    ok: variant.availability !== 'out_of_stock' && !soldOut[variant.sku],
  }));
  const okFor = (key: 'candle' | 'carrier', id: string, other: string | null) =>
    configs.some(
      (config) =>
        config.ok &&
        config[key] === id &&
        (!other || config[key === 'candle' ? 'carrier' : 'candle'] === other),
    );
  const anyFor = (key: 'candle' | 'carrier', id: string) =>
    configs.some((config) => config.ok && config[key] === id);
  const selected =
    configs.find(
      (config) => config.candle === choice.candle && config.carrier === choice.carrier,
    ) ?? null;
  const complete = Boolean(choice.candle && choice.carrier && selected?.ok);
  const added = selected !== null && addedSku === selected.sku;

  const stateOf = (key: 'candle' | 'carrier', id: string): OptionState => {
    const other = key === 'candle' ? choice.carrier : choice.candle;
    if (!okFor(key, id, other)) return 'off';
    return choice[key] === id ? 'on' : 'idle';
  };
  const pick = (key: 'candle' | 'carrier', id: string) => {
    // A blocked click only explains why; the "added" state stays (design pickCandle/pickCarrier).
    if (stateOf(key, id) === 'off') {
      setMessage(
        anyFor(key, id)
          ? 'Этот вариант недоступен с выбранными параметрами. Выберите другой'
          : 'Этого варианта сейчас нет в наличии. Выберите другой',
      );
      return;
    }
    setAddedSku(null);
    setMessage('');
    setChoice({ ...choice, [key]: id });
  };
  const reset = () => {
    setChoice({ candle: null, carrier: null });
    setMessage('');
    setAddedSku(null);
  };
  const submit = () => {
    if (!complete || !selected) {
      setMessage('Выберите все параметры');
      return;
    }
    add.mutate(
      { lines: [{ sku: selected.sku, quantity: 1, position: null }] },
      {
        onSuccess: () => {
          setAddedSku(selected.sku);
          setMessage('');
        },
        onError: (error) => {
          if (
            error instanceof ApiError &&
            (error.code === 'JUST_SOLD_OUT' || error.code === 'OUT_OF_STOCK')
          ) {
            setSoldOut((current) => ({ ...current, [selected.sku]: true }));
            setChoice({ ...choice, carrier: null });
            setMessage('Упс! Один из товаров только что закончился. Выберите другой вариант');
          } else setMessage(error.message);
        },
      },
    );
  };

  const optionName = (key: 'candle' | 'carrier', id: string | null) =>
    (key === 'candle' ? candleChoice : carrierChoice).options.find((option) => option.id === id) ??
    null;
  const candle = optionName('candle', choice.candle);
  const carrier = optionName('carrier', choice.carrier);
  const [mask, ...restFixed] = bundle.fixedItems;
  const rows = [
    { key: 'mask', text: mask?.name ?? '', image: mask?.image ?? null, placeholder: false },
    {
      key: 'candle',
      text: candle ? `Свеча, ${candle.short}` : 'Свеча — выберите размер',
      image: candle?.image ?? candleChoice.options[0]?.image ?? null,
      placeholder: !candle,
    },
    {
      key: 'carrier',
      text: carrier ? carrier.name : 'Ежедневник или тетрадь — выберите',
      image: carrier?.image ?? carrierChoice.options[0]?.image ?? null,
      placeholder: !carrier,
    },
    ...restFixed.map((item) => ({
      key: item.name,
      text: item.name,
      image: item.image,
      placeholder: false,
    })),
  ];

  const renderOptions = (key: 'candle' | 'carrier') => {
    const group = key === 'candle' ? candleChoice : carrierChoice;
    const label = key === 'candle' ? 'Размер свечи' : 'Ежедневник или тетрадь';
    return (
      <section className={styles.section} aria-label={label}>
        <h2 className={styles.h2}>{group.title}</h2>
        <fieldset className={styles.grid} aria-label={label}>
          {group.options.map((option) => {
            const state = stateOf(key, option.id);
            const other = key === 'candle' ? choice.carrier : choice.candle;
            const note =
              state === 'off'
                ? anyFor(key, option.id) && other
                  ? 'Недоступно с выбранными параметрами'
                  : 'Нет в наличии'
                : state === 'on'
                  ? 'Выбрано'
                  : '';
            return (
              <button
                key={option.id}
                type="button"
                className={styles.option}
                data-state={state}
                aria-pressed={state === 'on'}
                aria-disabled={state === 'off' || undefined}
                onClick={() => pick(key, option.id)}
              >
                <span className={styles.picture}>
                  {option.image && (
                    <Image
                      src={option.image}
                      alt=""
                      fill
                      sizes="(min-width: 1024px) 380px, 45vw"
                      className={styles.image}
                    />
                  )}
                </span>
                <span className={styles['option-text']}>
                  <span className={styles['option-name']}>{option.name}</span>
                  <span className={styles.note}>{note}</span>
                </span>
                {state === 'on' && (
                  <span className={styles.check} aria-hidden="true">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 14 14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      focusable="false"
                    >
                      <path d="M2.5 7.5l3 3 6-6" />
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </fieldset>
      </section>
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.main}>
        <div className={styles.intro}>
          <span className={styles.eyebrow}>Подарочный набор</span>
          <h1 className={styles.title}>{product.name}</h1>
          <p className={styles.lead}>{product.description}</p>
          <p className={styles.hint}>Выберите варианты двух товаров — остальное мы уже собрали</p>
          <output className={styles.message}>{message}</output>
        </div>
        <section className={styles.section} aria-label="В набор уже входят">
          <h2 className={styles.h2}>В набор уже входят</h2>
          <div className={styles.grid}>
            {bundle.fixedItems.map((item) => (
              <div key={item.name} className={styles.option} data-state="static">
                <span className={styles.picture}>
                  {item.image && (
                    <Image
                      src={item.image}
                      alt={item.name}
                      fill
                      sizes="(min-width: 1024px) 380px, 45vw"
                      className={styles.image}
                    />
                  )}
                </span>
                <span className={styles['option-text']}>
                  <span className={styles['option-name']}>{item.name}</span>
                </span>
              </div>
            ))}
          </div>
        </section>
        {renderOptions('candle')}
        {renderOptions('carrier')}
        <section className={styles.section} data-gap="14" aria-label="Подарки к набору">
          <div className={styles['gifts-head']}>
            <h2 className={styles.h2} data-size="28">
              Подарки к набору
            </h2>
            <Icon name="gift" size={24} strokeWidth={1.5} className={styles.gift} />
          </div>
          <div className={styles.grid}>
            {bundle.gifts.map((gift) => (
              <div key={gift.name} className={styles['gift-item']}>
                <span className={styles['gift-picture']}>
                  {gift.image && (
                    <Image
                      src={gift.image}
                      alt={gift.name}
                      fill
                      sizes="72px"
                      className={styles.image}
                    />
                  )}
                </span>
                <span className={styles['gift-name']}>{gift.name}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
      <aside className={styles.aside} aria-label="Ваш набор">
        <div className={styles.hero}>
          {bundle.heroImage && (
            <Image
              src={bundle.heroImage}
              alt="Подарочный набор J‑VU в открытой коробке"
              fill
              sizes="(min-width: 1024px) 400px, 100vw"
              className={styles.cover}
              priority
            />
          )}
        </div>
        <div className={styles['aside-body']}>
          <span className={styles['aside-title']}>Ваш набор</span>
          <ul className={styles.rows}>
            {rows.map((row) => (
              <li
                key={row.key}
                className={styles.row}
                data-placeholder={row.placeholder || undefined}
              >
                <span className={styles['row-picture']}>
                  {row.image && (
                    <Image src={row.image} alt="" fill sizes="40px" className={styles.image} />
                  )}
                </span>
                <span>{row.text}</span>
              </li>
            ))}
          </ul>
          <div className={styles['gift-note']}>{bundle.giftNote}</div>
          <div className={styles['price-row']}>
            <div className={styles.prices}>
              {product.compareAtPrice && (
                <span className={styles.old}>{formatMoney(product.compareAtPrice)}</span>
              )}
              <span className={styles.price}>
                {product.price ? formatMoney(product.price) : 'Цена уточняется'}
              </span>
            </div>
            <span className={styles.status} data-ok={complete || undefined}>
              {complete ? 'В наличии' : 'Выберите все параметры'}
            </span>
          </div>
          {added ? (
            <Link href="/cart" className={styles.cta}>
              Добавлено — перейти в корзину
            </Link>
          ) : (
            <button
              type="button"
              className={styles.cta}
              aria-disabled={!complete || undefined}
              aria-busy={add.isPending || undefined}
              disabled={add.isPending}
              onClick={submit}
            >
              Добавить в корзину
            </button>
          )}
          <div className={styles.foot}>
            <span>Набор добавится в корзину одной позицией</span>
            <button type="button" className={styles.reset} onClick={reset}>
              Сбросить выбор
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
